import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus } from 'lucide-react'

export interface AccordionItem {
  id?: string
  question: string
  answer: string | React.ReactNode
}

interface AccordionProps {
  items: AccordionItem[]
  numbered?: boolean
  defaultOpen?: number
}

export function Accordion({ items, numbered = true, defaultOpen }: AccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(
    typeof defaultOpen === 'number' ? defaultOpen : null,
  )

  return (
    <div className="w-full">
      {items.map((item, idx) => {
        const isOpen = openIndex === idx
        const itemId = item.id ?? `acc-item-${idx}`
        const buttonId = `${itemId}-trigger`
        const panelId = `${itemId}-panel`

        return (
          <motion.div
            key={itemId}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: idx * 0.05 }}
            className="border-b border-bruno/15 last:border-b-0"
          >
            <h3 className="m-0">
              <button
                id={buttonId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="group w-full flex items-start gap-6 md:gap-10 py-7 md:py-9 text-left transition-colors"
              >
                {numbered && (
                  <span
                    className={`shrink-0 mt-2 font-display italic text-sm md:text-base transition-colors ${
                      isOpen ? 'text-argan-deep' : 'text-bruno-muted group-hover:text-argan-deep'
                    }`}
                  >
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                )}

                <span
                  className={`flex-1 font-display text-bruno transition-colors ${
                    isOpen ? '' : 'group-hover:text-argan-deep'
                  }`}
                  style={{
                    fontSize: 'clamp(1.25rem, 2.2vw, 1.875rem)',
                    lineHeight: 1.2,
                    letterSpacing: '-0.01em',
                    fontStyle: isOpen ? 'italic' : 'normal',
                    fontWeight: isOpen ? 300 : 400,
                  }}
                >
                  {item.question}
                </span>

                <span
                  className={`shrink-0 mt-2 inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full border transition-all duration-500 ${
                    isOpen
                      ? 'bg-bruno border-bruno text-cream rotate-45'
                      : 'border-bruno/30 text-bruno group-hover:border-argan-deep group-hover:text-argan-deep'
                  }`}
                  aria-hidden="true"
                >
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                </span>
              </button>
            </h3>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  key="content"
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{
                    height: 'auto',
                    opacity: 1,
                    transition: {
                      height: { duration: 0.55, ease: [0.25, 1, 0.5, 1] },
                      opacity: { duration: 0.4, delay: 0.1 },
                    },
                  }}
                  exit={{
                    height: 0,
                    opacity: 0,
                    transition: {
                      height: { duration: 0.4, ease: [0.5, 0, 0.75, 0] },
                      opacity: { duration: 0.2 },
                    },
                  }}
                  className="overflow-hidden"
                >
                  <div
                    className={`pb-9 md:pb-12 ${
                      numbered ? 'md:ml-[calc(2.5rem+2.5rem)]' : ''
                    }`}
                  >
                    <div className="md:max-w-2xl text-bruno-soft text-base md:text-[1.0625rem] leading-relaxed whitespace-pre-line">
                      {item.answer}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )
      })}
    </div>
  )
}
